import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// The lair's meters, kept current while the page is open. Needs fall and
// vigour comes back on the clock, but the server only works them out when it
// draws, so a lair left open showed page-load's numbers until something was
// done. Now the panels ask again every minute while the tab is in view, and at
// once on coming back to it.
//
// A care act's own stream always wins: a poll that left before the act and
// lands after it would put the old numbers back for a minute, so its answer
// is dropped. And nothing is swapped while the keeper is in the care panel
// with the keyboard, which would pull the focus out from under them.
const EVERY_MS = 60_000
const STALE_MS = 15_000

export default class extends Controller {
  static values = { url: String }

  connect() {
    this.askedAt = Date.now()
    this.submits = 0
    this.acts = 0
    this.timer = setInterval(() => this.refresh(), EVERY_MS)

    this.onVisibilityChange = () => {
      if (!document.hidden && Date.now() - this.askedAt > STALE_MS) this.refresh()
    }
    this.onSubmitStart = () => { this.submits += 1 }
    this.onSubmitEnd = () => {
      this.submits = Math.max(0, this.submits - 1)
      this.acts += 1
    }
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    document.addEventListener("turbo:submit-start", this.onSubmitStart)
    document.addEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  disconnect() {
    clearInterval(this.timer)
    this.abort?.abort()
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    document.removeEventListener("turbo:submit-start", this.onSubmitStart)
    document.removeEventListener("turbo:submit-end", this.onSubmitEnd)
  }

  async refresh() {
    if (document.hidden || this.submits > 0 || this.inflight) return
    if (document.getElementById("care-panel")?.contains(document.activeElement)) return

    this.askedAt = Date.now()
    this.inflight = true
    this.abort = new AbortController()
    const acts = this.acts
    try {
      const url = new URL(this.urlValue, window.location.href)
      new URLSearchParams(window.location.search).forEach((value, key) => url.searchParams.set(key, value))
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        credentials: "same-origin",
        signal: this.abort.signal
      })
      if (!response.ok) return

      const html = await response.text()
      if (acts !== this.acts || this.submits > 0) return

      Turbo.renderStreamMessage(html)
    } catch {
      // Offline, or the page went: the next minute asks again.
    } finally {
      this.inflight = false
    }
  }
}
